import React, { memo, useCallback } from 'react';
import { ScrollDirection, useScrollDirection } from 'hooks/useScrollDirection';
import {
  selectErrorLabels,
  selectErroriInserimentoByTicket,
  selectIsFullCart,
  selectIsOpenCarrello,
  selectSliceTicketByType,
  selectTipo,
  selectVenditaAlertFailed,
  selectVenditaAlertSuccess,
} from 'features/carrello/carrelloSelectors';
import { selectTicketCashout, setTicketCashout } from 'features/dashboard/myBets/myBetSlice';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { venditaAlertFailedOpenChange, venditaAlertSuccessOpenChange } from 'features/carrello/carrelloSlice';

import { BannerInformation } from 'components/bannerInformation';
import { Button } from 'components/button';
import { Carrello } from 'features/carrello';
import { CarrelloCalcTicketTypes } from 'features/carrello/types';
import { CarrelloDialog } from 'features/carrello/carrelloDialog';
import { CarrelloDialogVenditaFailed } from 'features/carrello/carrelloDialogVenditaFailed/CarrelloDialogVenditaFailed';
import { CarrelloDialogVenditaSuccess } from 'features/carrello/carrelloDialogVenditaSuccess';
import { CarrelloTrigger } from 'features/carrello/carrelloTrigger';
import { CashoutDialog } from 'components/cashoutDialog';
import { ItemWithTranslation } from 'types/swagger';
import { Taxes } from 'features/sport/components/sportTicket/sportTicketSlice';
import classNames from 'classnames';
import { getSliceByTipoTicket } from 'features/carrello/utils';
import { importTicketByType } from 'features/carrello/actions';
import { isHappybetSite } from 'utility/constant';
import styles from './ScommesseLayout.module.scss';
import { useBlockScroll } from 'hooks/useBlockScroll';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import { useRouter } from 'next/router';
import useWindowSize from 'hooks/useWindowSize';

export type ScommesseLayoutProps = ItemWithTranslation<{ disciplina?: String }> & {
  taxes?: Taxes;
  children?: React.ReactNode;
  className?: string;
  hasNoPaddingOnMain?: boolean;
};

export const ScommesseLayout = memo(function ScommesseLayout({
  taxes,
  children,
  className,
  hasNoPaddingOnMain,
}: ScommesseLayoutProps) {
  const dispatch = useAppDispatch();
  const { isReady } = useRouter();

  const isOpen = useTypedSelector(selectIsOpenCarrello);
  const isFullCart = useTypedSelector(selectIsFullCart);
  const errorsLabels = useTypedSelector(selectErrorLabels);

  const tipo = useTypedSelector(selectTipo);
  const { actions } = getSliceByTipoTicket(tipo);
  const sliceTicket = useTypedSelector((state) => selectSliceTicketByType(state, tipo));
  const { importTicketAlertState } = sliceTicket;

  const venditaAlertSuccess = useTypedSelector(selectVenditaAlertSuccess);
  const venditaAlertFailed = useTypedSelector(selectVenditaAlertFailed);
  const erroriInserimento = useTypedSelector((state) => selectErroriInserimentoByTicket(state, tipo));
  const ticketCashout = useTypedSelector(selectTicketCashout);

  const { isMobile } = useWindowSize();

  const [
    titleCashout,
    subtitleCashout,
    errorTitleCashout,
    labelAnnulla,
    labelContinua,
    importTicketTitle,
    importTicketSubtitle,
  ] = useGetLabelsByKeys([
    'cashout-dialog-title',
    'cashout-dialog-subtitle',
    'cashout-dialog-error-title',
    'annulla',
    'continua',
    'import-ticket-dialog-title',
    'import-ticket-dialog-subtitle',
  ]);

  // TODO: TRANFORMARE IN THUNK
  const importTicketShared = useCallback(
    (ticket?: CarrelloCalcTicketTypes) => {
      if (ticket) {
        dispatch(importTicketByType(tipo, ticket));
      }
    },
    [dispatch, tipo]
  );

  useBlockScroll(isOpen && isMobile && isFullCart);

  const { scrollDir } = useScrollDirection();

  const renderCarrello = useCallback((): JSX.Element => {
    const key = 'carrello';

    if (!isReady) return <React.Fragment key={key} />;

    if (isOpen) {
      return (
        <div key={key} className={classNames(styles.cart, { [styles.scrolledUp]: scrollDir === ScrollDirection.UP })}>
          {isReady && <Carrello taxes={taxes} />}
        </div>
      );
    } else if (!(isHappybetSite && isMobile)) {
      return <CarrelloTrigger key={key} />;
    }

    return <React.Fragment key={key} />;
  }, [isReady, isOpen, isMobile]);

  return (
    <React.Fragment>
      <section className={classNames(styles.container, className ?? '', { [styles.open]: isOpen })}>
        <div
          className={`${styles.mainWrapper} ${hasNoPaddingOnMain && styles.noPadding} ${isOpen ? styles.open : ''}`}
          data-parent
        >
          {erroriInserimento && (
            <BannerInformation
              text={
                [errorsLabels.find((errorLabel) => errorLabel.key === erroriInserimento)]?.[0]?.value ||
                erroriInserimento
              }
              type="warning"
              layoutType="big"
              visible={erroriInserimento ? true : false}
              className={styles.banner}
            />
          )}
          {children}
        </div>
        {renderCarrello()}
      </section>
      {venditaAlertSuccess && (
        <CarrelloDialogVenditaSuccess
          open={true}
          onOpenChange={() => dispatch(venditaAlertSuccessOpenChange(!venditaAlertSuccess))}
        />
      )}
      {venditaAlertFailed.isOpen && (
        <CarrelloDialogVenditaFailed
          open={true}
          onOpenChange={() => dispatch(venditaAlertFailedOpenChange(!venditaAlertFailed.isOpen))}
          error={venditaAlertFailed.typeError}
        />
      )}
      {importTicketAlertState.isOpen && (
        <CarrelloDialog
          open={true}
          title={importTicketTitle}
          type="question"
          text={importTicketSubtitle}
          onOpenChange={() => dispatch(actions.importTicketDialogOpenChange(false))}
        >
          <div className={styles.buttonsDialog}>
            <Button
              visualizationType="tertiary"
              onClick={() => dispatch(actions.importTicketDialogChange({ isOpen: false, ticket: undefined }))}
            >
              {labelAnnulla}
            </Button>
            <Button visualizationType="secondary" onClick={() => importTicketShared(importTicketAlertState?.ticket)}>
              {labelContinua}
            </Button>
          </div>
        </CarrelloDialog>
      )}
      {!!ticketCashout?.idBiglietto && (
        <CashoutDialog
          open={true}
          onOpenChange={() => dispatch(setTicketCashout({ idBiglietto: undefined, importo: undefined }))}
          idBiglietto={ticketCashout.idBiglietto}
          title={titleCashout}
          titleError={errorTitleCashout}
          text={subtitleCashout}
        />
      )}
    </React.Fragment>
  );
});
